import type { ProspectMatterType, ProspectPracticeArea, TemplateType } from '@common/enums';
import type { TemplateFormField } from '@common/types/templateFormFields';
import type { FormGroup, TemplateFormMetadata } from '@common/types/templateForms';
import { getMatterTypeLabel, getPracticeAreaLabel } from '@common/util';
import { resolveFormGroups, syncFieldGroupTitles } from '@common/utils/formGroups';
import { getIsIntakeFormV2 } from '@common/utils/intakeFormVersion';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    Radio,
    RadioGroup,
    Typography,
} from '@mui/material';
import { useState } from 'react';
import SearchableLawFirmSelect, {
    type LawFirmSelectValue,
} from '@/components/SearchableLawFirmSelect';
import { saveQuestionsAsDefault } from '@/services/QuestionsService';
import { errorSnackbar, successSnackbar } from '@/utils/snackbar';

export interface DefaultIntakeDesign {
    intakeFormDescription: string;
    textAfterSubmitIntakeForClient: string;
    templateFormFields: TemplateFormField[];
    formGroups?: FormGroup[];
    metadata?: TemplateFormMetadata | null;
}

interface Props {
    practiceArea: ProspectPracticeArea | null;
    matterType: ProspectMatterType | null;
    templateType: TemplateType;
    defaultLawFirmId?: number;
    // Called on save so callers without the form loaded (e.g. the templates list) can fetch it
    getIntakeDesign: () => DefaultIntakeDesign | Promise<DefaultIntakeDesign>;
    onClose: () => void;
}

type SaveScope = 'Global' | 'LawFirm';

export default function SaveAsDefaultDialog({
    practiceArea,
    matterType,
    templateType,
    defaultLawFirmId,
    getIntakeDesign,
    onClose,
}: Props) {
    const [isLoading, setIsLoading] = useState(false);
    const [saveScope, setSaveScope] = useState<SaveScope>('Global');
    const [selectedLawFirmId, setSelectedLawFirmId] =
        useState<LawFirmSelectValue>(defaultLawFirmId);

    const handleSave = async () => {
        if (saveScope === 'LawFirm' && !selectedLawFirmId) {
            errorSnackbar('Please select a law firm');
            return;
        }

        if (!practiceArea || !matterType) {
            errorSnackbar('Practice area and matter type are required');
            return;
        }

        setIsLoading(true);
        try {
            const intakeDesign = await getIntakeDesign();

            // Store both group title copies as the builder shows them
            const fields = intakeDesign.templateFormFields.map(syncFieldGroupTitles);
            const questions = fields.map((field) => ({
                name: field.name || field.questionSchema.name,
                title: field.title,
                subtitle: field.subTitle || '',
                questionSchema: field.questionSchema,
                questionValidationSchema: field.questionValidationSchema ?? null,
                displayOrder: field.displayOrder,
                isVisibleToUser: field.isVisibleToUser,
                isLocked: field.isLocked ?? false,
                formGroupTitle: field.formGroupTitle || '',
                overridenOfQuestion: null,
                lawFirmEntityMap: '',
                isRequiredForFullAutomation:
                    field.questionSchema.isRequiredForFullAutomation ?? false,
            }));

            const formGroups = getIsIntakeFormV2(practiceArea, matterType)
                ? resolveFormGroups(intakeDesign.formGroups, fields)
                : undefined;

            const payload = {
                lawFirmId: saveScope === 'Global' ? null : (selectedLawFirmId as number),
                matterType,
                templateType,
                templateFormType: 'IntakeForm' as const,
                practiceArea,
                questions,
                headerDescription: intakeDesign.intakeFormDescription,
                footerTemplate: intakeDesign.textAfterSubmitIntakeForClient,
                formGroups,
                metadata: intakeDesign.metadata,
            };

            await saveQuestionsAsDefault(payload);
            successSnackbar(
                saveScope === 'Global'
                    ? 'Questions saved as global defaults'
                    : 'Questions saved as defaults for the selected law firm',
            );
            onClose();
        } catch (error: any) {
            console.error(error);
            errorSnackbar(error.response?.data || 'Failed to save questions as default');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Dialog open onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Save as Default Questions</DialogTitle>
            <DialogContent>
                <Alert severity="info" sx={{ mb: 3 }}>
                    This will save the current intake form questions and form settings as defaults
                    for{' '}
                    <strong>
                        {`${getPracticeAreaLabel(practiceArea)} - ${getMatterTypeLabel(matterType)}`}
                    </strong>{' '}
                    intake forms.
                </Alert>

                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Save scope
                </Typography>
                <FormControl component="fieldset" sx={{ mb: 2, width: '100%' }}>
                    <RadioGroup
                        value={saveScope}
                        onChange={(e) => setSaveScope(e.target.value as SaveScope)}
                    >
                        <FormControlLabel
                            value="Global"
                            control={<Radio />}
                            label="Global (all law firms will use these defaults)"
                        />
                        <FormControlLabel
                            value="LawFirm"
                            control={<Radio />}
                            label="Specific law firm only"
                        />
                    </RadioGroup>
                </FormControl>

                {saveScope === 'LawFirm' && (
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            Select law firm
                        </Typography>
                        <SearchableLawFirmSelect
                            value={selectedLawFirmId}
                            onChange={setSelectedLawFirmId}
                            allOptionLabel="Select a law firm..."
                            includeTestFirms={true}
                        />
                    </Box>
                )}

                <Alert severity="warning" sx={{ mt: 3 }}>
                    <Typography variant="body2">
                        <strong>Warning:</strong> This will replace any existing defaults for this
                        practice area and matter type
                        {saveScope === 'LawFirm'
                            ? ' for the selected law firm. New templates for this law firm will use these defaults instead of the global defaults'
                            : ' globally'}
                        .
                    </Typography>
                </Alert>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isLoading}>
                    Cancel
                </Button>
                <Button variant="contained" onClick={handleSave} disabled={isLoading}>
                    {isLoading ? 'Saving...' : 'Save as Default'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
